import type { ValidationReport } from '../types/result.js';

/**
 * Geometry audit rows.
 *
 * One place that turns the normalized geometry into the numbers a human (and the
 * calibration tests) can check against a stationary validator:
 *
 *   image size → head top → chin → head height → head ratio
 *              → left/right eye → eye centre → eye ratio → roll/yaw/pitch
 *
 * Everything is computed in the **original image coordinate system**: normalized
 * values are multiplied by the original pixel size, never by a canvas, viewport or
 * preview size. `headHeightPx` is literally `chinPx - headTopPx`.
 */

export interface GeometryAuditRow {
  key: string;
  label: string;
  /** Value in the original image pixels (or degrees for angles). */
  pixels?: number;
  /** Ratio of the original image the value covers (0…1). */
  ratio?: number;
  /** Degrees, for the pose rows. */
  degrees?: number;
  unit: 'px' | '%' | 'deg';
  /** Extra context: the eye pair, the source of the head top, confidence. */
  note?: string;
}

export interface GeometryAudit {
  /** Original image size, the coordinate system every number below lives in. */
  imageSize: { width: number; height: number };
  rows: GeometryAuditRow[];
  /** Convenience accessors used by the geometry calibration tests. */
  values: {
    headTopPx?: number;
    chinPx?: number;
    headHeightPx?: number;
    headRatio?: number;
    eyeCenterPx?: number;
    eyeRatioFromTop?: number;
    eyeRatioFromBottom?: number;
    leftEyePx?: { x: number; y: number };
    rightEyePx?: { x: number; y: number };
    /**
     * Face centre in original-image pixels, matching the manual ground truth of
     * the real-photo calibration set: **Y is the midpoint of the eye line** (the
     * same definition the human marking uses), X is the product's horizontal face
     * centre (`faceCenterX`, a blend of the eye centre and the head-box centre —
     * it agrees with the eye midpoint within 0.25 % of the frame width on the
     * calibration set, so the manual and product definitions coincide there).
     *
     * Y used to be the head-box midpoint, which is a *vertical framing* metric,
     * not the face centre the ground truth describes: comparing the two made
     * 4 of 20 calibration frames look wrong (up to 4.6 % of the frame height)
     * while nothing was actually mis-measured.
     */
    faceCenterPx?: { x: number; y: number };
    /** Confidence of the head-top estimate (0..1). */
    headTopConfidence?: number;
    /**
     * The three head-height estimators in original-image pixels. Diagnostics for
     * the real-photo calibration: they show which estimator drives the bias.
     */
    headTopEstimatorsPx?: { mesh: number; eyeLine: number; faceMesh: number };
    /** The plausibility band the blended estimate was clamped into, in pixels. */
    headTopBoundsPx?: { lowerBound: number; upperBound: number; clamped: 'none' | 'lower' | 'upper' };
    /** What the silhouette refinement did, and why. */
    headTopSilhouette?: { decision: 'accepted' | 'rejected' | 'not-run'; reason?: string };
    /** Where the eye centres came from (iris landmarks are the precise path). */
    eyeCentersSource?: 'iris' | 'contour';

    // --- EXPERIMENTAL (diagnostic only): visible chin vs landmark 152 -------
    // None of these numbers is used by a check; they exist so the calibration
    // suite and `?debug=1` can compare the two chin definitions.
    /** Landmark 152 (the chin the product measures), original-image pixels. */
    y152Px?: number;
    /** Visible chin contour from the pixels, original-image pixels. */
    visibleChinY?: number;
    visibleChinSource?: 'shadow' | 'beard' | 'landmark' | 'none';
    visibleChinConfidence?: number;
    /** Depth of the under-chin luma dip (luma units). */
    shadowDepth?: number;
    saturationAt152?: number;
    /** NaN when no contour was found. */
    saturationRecovery?: number;
    /** visibleChinY − y152, in original-image pixels. */
    visibleChinDeltaPx?: number;
    /** The same difference as a share of the image height. */
    visibleChinDeltaPercentH?: number;
    /** Head height as the product measures it today (landmark 152). */
    currentHeadHeightPx?: number;
    /** Head height the experimental chin would give — NOT used for any verdict. */
    experimentalHeadHeightPx?: number;
    /** Experimental head ratio, for the document-boundary analysis only. */
    experimentalHeadRatio?: number;
    roll?: number;
    yaw?: number;
    pitch?: number;
  };
  /** Why the head top is what it is (mesh extrapolation / silhouette / notes). */
  headTopSource?: string;
  headTopNotes: string[];
  headTopConfidence?: number;
}

const px = (value: number, size: number): number => Math.round(value * size * 10) / 10;
const pct = (value: number): number => Math.round(value * 1000) / 10;

/** Builds the audit view of one report; empty rows when no face was measured. */
export const buildGeometryAudit = (report: ValidationReport): GeometryAudit => {
  const imageSize = { width: report.image.width, height: report.image.height };
  const geometry = report.metrics.geometry;
  const source = geometry?.imageSize ?? imageSize;
  if (!geometry) {
    return {
      imageSize,
      rows: [
        {
          key: 'image',
          label: 'Размер изображения',
          pixels: imageSize.width,
          unit: 'px',
          note: `${imageSize.width} × ${imageSize.height}`,
        },
      ],
      values: {},
      headTopNotes: ['geometry-unavailable'],
    };
  }

  const chinY = geometry.chinPoint.y;
  const headTopY = geometry.headTopPoint.y;
  const headHeight = chinY - headTopY;
  const eyeCenterY = geometry.eyeCenter.y;

  const rows: GeometryAuditRow[] = [
    {
      key: 'image',
      label: 'Размер изображения',
      pixels: source.width,
      unit: 'px',
      note: `${source.width} × ${source.height}`,
    },
    {
      key: 'head_top',
      label: 'Макушка',
      pixels: px(headTopY, source.height),
      ratio: headTopY,
      unit: 'px',
      ...(geometry.headTopSource ? { note: geometry.headTopSource } : {}),
    },
    {
      key: 'chin',
      label: 'Подбородок',
      pixels: px(chinY, source.height),
      ratio: chinY,
      unit: 'px',
    },
    {
      key: 'head_height',
      label: 'Высота головы',
      pixels: px(headHeight, source.height),
      ratio: headHeight,
      unit: 'px',
      note: 'chin − head top',
    },
    {
      key: 'head_ratio',
      label: 'Высота головы, % кадра',
      ratio: headHeight,
      pixels: pct(headHeight),
      unit: '%',
    },
    {
      key: 'eye_left',
      label: 'Левый глаз',
      pixels: px(geometry.eyeLeft.y, source.height),
      unit: 'px',
      note: `x ${px(geometry.eyeLeft.x, source.width)}`,
    },
    {
      key: 'eye_right',
      label: 'Правый глаз',
      pixels: px(geometry.eyeRight.y, source.height),
      unit: 'px',
      note: `x ${px(geometry.eyeRight.x, source.width)}`,
    },
    {
      key: 'eye_center',
      label: 'Центр глаз',
      pixels: px(eyeCenterY, source.height),
      ratio: eyeCenterY,
      unit: 'px',
    },
    {
      key: 'eye_ratio',
      label: 'Линия глаз, % от низа',
      ratio: geometry.eyeLineFromBottom,
      pixels: pct(geometry.eyeLineFromBottom),
      unit: '%',
    },
    {
      key: 'face_center',
      label: 'Центр лица (середина глаз)',
      pixels: px(eyeCenterY, source.height),
      unit: 'px',
      note: `x ${px(geometry.faceCenterX, source.width)} (по X — продуктовый центр, по Y — середина линии глаз)`,
    },
    ...(geometry.visibleChin
      ? [
          {
            key: 'visible_chin',
            label: 'Видимый подбородок (эксперимент)',
            pixels: px(geometry.visibleChin.y, source.height),
            unit: 'px' as const,
            note:
              `источник ${geometry.visibleChin.source}, confidence ${geometry.visibleChin.confidence}, ` +
              `Δ ${px(geometry.visibleChin.y - chinY, source.height)} px; ` +
              `landmark 152 = ${px(chinY, source.height)}; ` +
              `высота головы сейчас ${px(headHeight, source.height)} px, ` +
              `экспериментальная ${px(geometry.visibleChin.y - headTopY, source.height)} px (на вердикты не влияет); ` +
              geometry.visibleChin.note,
          },
        ]
      : []),
    {
      key: 'head_top_confidence',
      label: 'Уверенность макушки',
      pixels: geometry.headTopConfidence.value,
      unit: '%',
      note: geometry.headTopConfidence.reason ?? geometry.headTopSource,
    },
    {
      key: 'roll',
      label: 'Наклон головы (roll)',
      degrees: geometry.roll,
      unit: 'deg',
      note: 'по линии глаз',
    },
    { key: 'yaw', label: 'Поворот головы (yaw)', degrees: geometry.yaw, unit: 'deg' },
    { key: 'pitch', label: 'Наклон (pitch)', degrees: geometry.pitch, unit: 'deg' },
  ];

  return {
    imageSize,
    rows,
    values: {
      headTopPx: px(headTopY, source.height),
      chinPx: px(chinY, source.height),
      headHeightPx: px(headHeight, source.height),
      headRatio: headHeight,
      eyeCenterPx: px(eyeCenterY, source.height),
      eyeRatioFromTop: eyeCenterY,
      eyeRatioFromBottom: geometry.eyeLineFromBottom,
      leftEyePx: { x: px(geometry.eyeLeft.x, source.width), y: px(geometry.eyeLeft.y, source.height) },
      rightEyePx: {
        x: px(geometry.eyeRight.x, source.width),
        y: px(geometry.eyeRight.y, source.height),
      },
      faceCenterPx: {
        // X: the product's horizontal face centre (used by `face_position`).
        // Y: the eye-line midpoint — the definition the manual ground truth uses.
        x: px(geometry.faceCenterX, source.width),
        y: px(eyeCenterY, source.height),
      },
      headTopConfidence: geometry.headTopConfidence.value,
      ...(geometry.headTopEstimators
        ? {
            headTopEstimatorsPx: {
              mesh: px(geometry.headTopEstimators.mesh, source.height),
              eyeLine: px(geometry.headTopEstimators.eyeLine, source.height),
              faceMesh: px(geometry.headTopEstimators.faceMesh, source.height),
            },
          }
        : {}),
      ...(geometry.headTopBounds
        ? {
            headTopBoundsPx: {
              lowerBound: px(geometry.headTopBounds.lowerBound, source.height),
              upperBound: px(geometry.headTopBounds.upperBound, source.height),
              clamped: geometry.headTopBounds.clamped,
            },
          }
        : {}),
      ...(geometry.headTopSilhouette
        ? {
            headTopSilhouette: {
              decision: geometry.headTopSilhouette.decision,
              ...(geometry.headTopSilhouette.reason
                ? { reason: geometry.headTopSilhouette.reason }
                : {}),
            },
          }
        : {}),
      ...(geometry.eyeCentersSource ? { eyeCentersSource: geometry.eyeCentersSource } : {}),
      ...(geometry.visibleChin
        ? {
            y152Px: px(chinY, source.height),
            visibleChinY: px(geometry.visibleChin.y, source.height),
            visibleChinSource: geometry.visibleChin.source,
            visibleChinConfidence: geometry.visibleChin.confidence,
            shadowDepth: Math.round(geometry.visibleChin.shadowDepth * 10) / 10,
            saturationAt152: Math.round(geometry.visibleChin.saturationAt152 * 1000) / 1000,
            saturationRecovery: Number.isNaN(geometry.visibleChin.saturationRecovery)
              ? undefined
              : Math.round(geometry.visibleChin.saturationRecovery * 1000) / 1000,
            visibleChinDeltaPx: px(geometry.visibleChin.y - chinY, source.height),
            visibleChinDeltaPercentH: pct(geometry.visibleChin.y - chinY),
            currentHeadHeightPx: px(headHeight, source.height),
            experimentalHeadHeightPx: px(geometry.visibleChin.y - headTopY, source.height),
            experimentalHeadRatio: Math.round((geometry.visibleChin.y - headTopY) * 1000) / 1000,
          }
        : {}),
      roll: geometry.roll,
      yaw: geometry.yaw,
      pitch: geometry.pitch,
    },
    ...(geometry.headTopSource ? { headTopSource: geometry.headTopSource } : {}),
    headTopNotes: geometry.headTopNotes ?? [],
    ...(geometry.headTopConfidence ? { headTopConfidence: geometry.headTopConfidence.value } : {}),
  };
};

/** Compact one-line summary, handy in test output and the debug panel. */
export const formatGeometryAudit = (audit: GeometryAudit): string => {
  const { values } = audit;
  if (values.headRatio === undefined) return 'geometry: n/a';
  return [
    `image ${audit.imageSize.width}×${audit.imageSize.height}`,
    `head top ${values.headTopPx}`,
    `chin ${values.chinPx}`,
    `head ${values.headHeightPx}px`,
    `head ratio ${(values.headRatio * 100).toFixed(1)}%`,
    `eye centre ${values.eyeCenterPx}`,
    `eye ratio ${((values.eyeRatioFromBottom ?? 0) * 100).toFixed(1)}% from bottom`,
    `face centre ${values.faceCenterPx ? `${values.faceCenterPx.x}×${values.faceCenterPx.y}` : '—'}`,
    `roll ${(values.roll ?? 0).toFixed(1)}°`,
    `yaw ${(values.yaw ?? 0).toFixed(1)}°`,
    `pitch ${(values.pitch ?? 0).toFixed(1)}°`,
    `confidence ${(values.headTopConfidence ?? 0).toFixed(2)}`,
    `source ${audit.headTopSource ?? 'n/a'}`,
    values.headTopEstimatorsPx
      ? `estimators mesh ${values.headTopEstimatorsPx.mesh}px eyeLine ${values.headTopEstimatorsPx.eyeLine}px faceMesh ${values.headTopEstimatorsPx.faceMesh}px`
      : 'estimators n/a',
    values.headTopBoundsPx
      ? `bounds ${values.headTopBoundsPx.lowerBound}…${values.headTopBoundsPx.upperBound}px clamped ${values.headTopBoundsPx.clamped}`
      : 'bounds n/a',
    `silhouette ${values.headTopSilhouette?.decision ?? 'not-run'}${
      values.headTopSilhouette?.reason ? ` (${values.headTopSilhouette.reason})` : ''
    }`,
    `eyes ${values.eyeCentersSource ?? 'n/a'}`,
    `guards ${audit.headTopNotes.length > 0 ? audit.headTopNotes.join(',') : 'none'}`,
  ].join(' | ');
};
